import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { TicketEditor } from "./TicketEditor";
import { crearTicket, type TicketPriority } from "../../services/ticketsService";
import { PRIORITY_OPTIONS, PRIORITY_LABELS } from "./ticketBadges";
import { MODULOS } from "./moduloCatalog";

interface NuevoTicketDialogProps {
  onCreated: () => void;
}

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };
const OTRA_SECCION = "__otra__";

function isEmptyDoc(doc: any): boolean {
  if (!doc?.content) return true;
  return doc.content.every(
    (node: any) => node.type === "paragraph" && !node.content?.length,
  );
}

export function NuevoTicketDialog({ onCreated }: NuevoTicketDialogProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("media");
  const [moduloKey, setModuloKey] = useState("");
  const [seccionKey, setSeccionKey] = useState("");
  const [seccionLibre, setSeccionLibre] = useState("");
  const [content, setContent] = useState<unknown>(EMPTY_DOC);
  const [saving, setSaving] = useState(false);

  const modulo = MODULOS.find((m) => m.key === moduloKey);
  const requiereSeccionLibre = !modulo || modulo.secciones.length === 0 || seccionKey === OTRA_SECCION;

  const reset = () => {
    setTitle("");
    setPriority("media");
    setModuloKey("");
    setSeccionKey("");
    setSeccionLibre("");
    setContent(EMPTY_DOC);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Ponle un título al ticket");
      return;
    }
    if (!moduloKey) {
      toast.error("Indica en qué módulo está el problema");
      return;
    }
    if (isEmptyDoc(content)) {
      toast.error("Describe el problema antes de enviarlo");
      return;
    }
    const seccionLabel = requiereSeccionLibre
      ? seccionLibre.trim() || undefined
      : modulo?.secciones.find((s) => s.key === seccionKey)?.label;

    setSaving(true);
    try {
      await crearTicket({
        title: title.trim(),
        content,
        priority,
        module: modulo?.label,
        section: seccionLabel,
      });
      toast.success("Ticket creado");
      reset();
      setOpen(false);
      onCreated();
    } catch (err: any) {
      toast.error("No se pudo crear el ticket: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Reportar bug</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reportar un bug</DialogTitle>
          <DialogDescription>
            Entre más detalle (texto, capturas, video), más rápido se puede resolver.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-title">Título</Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Resumen corto del problema"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="space-y-1.5">
              <Label>Prioridad</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Módulo</Label>
              <Select
                value={moduloKey}
                onValueChange={(v) => {
                  setModuloKey(v);
                  setSeccionKey("");
                  setSeccionLibre("");
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="¿Dónde está el problema?" />
                </SelectTrigger>
                <SelectContent>
                  {MODULOS.map((m) => (
                    <SelectItem key={m.key} value={m.key}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {modulo && modulo.secciones.length > 0 && (
              <div className="space-y-1.5">
                <Label>Sección</Label>
                <Select value={seccionKey} onValueChange={setSeccionKey}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[28rem]">
                    {modulo.secciones.map((s) => (
                      <SelectItem key={s.key} value={s.key} className="whitespace-normal">
                        {s.label}
                      </SelectItem>
                    ))}
                    <SelectItem value={OTRA_SECCION}>Otra...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {requiereSeccionLibre && moduloKey && (
              <div className="space-y-1.5">
                <Label>{modulo && modulo.secciones.length > 0 ? "Especifica la sección" : "Sección (opcional)"}</Label>
                <Input
                  className="w-56"
                  value={seccionLibre}
                  onChange={(e) => setSeccionLibre(e.target.value)}
                  placeholder="Ej. NOM-A-073, tab de contactos..."
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <TicketEditor onChange={setContent} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Enviando..." : "Enviar ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
